import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Field from '../../components/Field';
import {
  Building2, Plus, X, Edit, History, CreditCard,
  Copy, Check, Key, ShieldAlert, Search, RefreshCw,
  Ban, ShieldCheck, AlertTriangle, BarChart2, Trash2, Clock,
  Package, Users, ShoppingCart, TrendingUp,
  Database, RotateCcw, Flame,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public`;

const EMPTY_FORM = { id: null, name: '', plan: '', admin_email: '', admin_password: '', paid_until: '', schema_name: '' };
const EMPTY_CRED_RESET = { newPassword: '', confirm: '' };

export default function TenantsManager() {
  const [tenants, setTenants] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modales
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [credModal, setCredModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { tenant } — confirm suspend/activate
  const [deleteModal, setDeleteModal] = useState(null);   // { tenant } — confirm delete
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [successModal, setSuccessModal] = useState(null); // { name, access_code, admin_email, admin_password }
  const [credSuccessModal, setCredSuccessModal] = useState(null); // { tenantName, email, password }
  const [adminInfo, setAdminInfo] = useState(null); // { username, email } del admin del tenant seleccionado
  const [statsModal, setStatsModal] = useState(null); // { tenant, data } | null
  const [statsLoading, setStatsLoading] = useState(false);

  // Schemas dormidos (sin registro activo de Tenant)
  const [dormantSchemas, setDormantSchemas] = useState([]);
  const [dormantLoading, setDormantLoading] = useState(false);
  const [purgingSchema, setPurgingSchema] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [credForm, setCredForm] = useState(EMPTY_CRED_RESET);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [copied, setCopied] = useState('');


  const fetchData = async () => {
    try {
      const [rt, rp] = await Promise.all([
        axios.get(`${BASE}/tenants/`),
        axios.get(`${BASE}/planes/`),
      ]);
      setTenants(rt.data);
      setPlanes(rp.data);
    } catch {
      setError('Error al cargar información. Verificá tu sesión.');
    }
  };

  const fetchDormant = async () => {
    setDormantLoading(true);
    try {
      const res = await axios.get(`${BASE}/dormant-schemas/`);
      setDormantSchemas(res.data.dormant_schemas || []);
    } catch {
      // silencioso
    } finally {
      setDormantLoading(false);
    }
  };

  const purgeSchema = async (schema) => {
    if (!window.confirm(`¿Purgar permanentemente el schema "${schema}"? Se perderán TODOS los datos.`)) return;
    setPurgingSchema(schema);
    try {
      await axios.delete(`${BASE}/dormant-schemas/`, { data: { schema_name: schema } });
      setDormantSchemas(prev => prev.filter(s => s !== schema));
    } catch {
      alert('Error al purgar el schema.');
    } finally {
      setPurgingSchema(null);
    }
  };

  useEffect(() => { fetchData(); fetchDormant(); }, []);

  const copy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // ── CREAR ──────────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${BASE}/tenants/`, {
        name: form.name,
        plan: form.plan ? parseInt(form.plan) : null,
        admin_email: form.admin_email,
        admin_password: form.admin_password || undefined,
        paid_until: form.paid_until || undefined,
        schema_name: form.schema_name || undefined,
      });
      setCreateModal(false);
      setForm(EMPTY_FORM);
      setSuccessModal(res.data.credentials
        ? { name: res.data.name, ...res.data.credentials }
        : null
      );
      fetchData();
      fetchDormant();
    } catch (err) {
      const msg = err.response?.data?.name?.[0]
        || err.response?.data?.admin_email?.[0]
        || err.response?.data?.error
        || err.response?.data?.detail
        || 'Error al crear el inquilino.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── EDITAR ─────────────────────────────────────────────────────────────────
  const openEdit = (t) => {
    // Convertir plan a string para que el <select> haga match correcto con los valores de las opciones
    setForm({ id: t.id, name: t.name, plan: t.plan != null ? String(t.plan) : '', admin_email: '', admin_password: '', paid_until: t.paid_until ? t.paid_until.slice(0, 10) : '' });
    setSelectedTenant(t);
    setError(null);
    setEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await axios.put(`${BASE}/tenants/${form.id}/`, { name: form.name, plan: form.plan ? parseInt(form.plan) : null, paid_until: form.paid_until || '' });
      setEditModal(false);
      fetchData();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || d?.detail || 'Error al actualizar el inquilino.');
    } finally {
      setLoading(false);
    }
  };

  // ── TOGGLE ACTIVO ──────────────────────────────────────────────────────────
  const handleToggle = (t) => {
    setConfirmModal({ tenant: t });
  };

  const executeToggle = async () => {
    const t = confirmModal?.tenant;
    if (!t) return;
    setLoading(true);
    try {
      await axios.post(`${BASE}/tenants/${t.id}/toggle_active/`, {});
      setConfirmModal(null);
      fetchData();
    } catch {
      setError('Error al cambiar el estado del inquilino.');
      setConfirmModal(null);
    } finally {
      setLoading(false);
    }
  };

  // ── ARCHIVAR TENANT (elimina registro, conserva schema) ────────────────────
  const executeDelete = async () => {
    const t = deleteModal?.tenant;
    if (!t) return;
    setLoading(true);
    try {
      await axios.delete(`${BASE}/tenants/${t.id}/`);
      setDeleteModal(null);
      setDeleteConfirmText('');
      fetchData();
      fetchDormant();  // actualizar lista de schemas dormidos
    } catch {
      setError('Error al archivar el inquilino.');
      setDeleteModal(null);
      setDeleteConfirmText('');
    } finally {
      setLoading(false);
    }
  };

  // ── RESETEAR CONTRASEÑA ADMIN TENANT ──────────────────────────────────────
  const openCredReset = async (t) => {
    setSelectedTenant(t);
    setCredForm(EMPTY_CRED_RESET);
    setAdminInfo(null);
    setError(null);
    setCredModal(true);
    // Cargar email del admin del tenant
    try {
      const res = await axios.get(`${BASE}/tenants/${t.id}/admin_credentials/`);
      setAdminInfo(res.data);
    } catch {
      // No bloquea el modal si falla; sólo no mostramos el email
    }
  };

  const handleCredReset = async (e) => {
    e.preventDefault();
    if (credForm.newPassword !== credForm.confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${BASE}/tenants/${selectedTenant.id}/admin_credentials/`, { new_password: credForm.newPassword });
      setCredModal(false);
      setCredSuccessModal({
        tenantName: selectedTenant.name,
        email: adminInfo?.email || '—',
        password: credForm.newPassword,
      });
      setCredForm(EMPTY_CRED_RESET);
    } catch {
      setError('Error al actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  // ── ESTADÍSTICAS DE TENANT ────────────────────────────────────────────────
  const openStats = async (t) => {
    setStatsModal({ tenant: t, data: null });
    setStatsLoading(true);
    try {
      const res = await axios.get(`${BASE}/tenants/${t.id}/stats/`);
      setStatsModal({ tenant: t, data: res.data });
    } catch {
      setStatsModal({ tenant: t, data: 'error' });
    } finally {
      setStatsLoading(false);
    }
  };

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.access_code || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-brand-50 p-4 rounded-2xl text-brand-800"><Building2 size={24} /></div>
          <div>
            <h2 className="titulo-modal">Inquilinos</h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Gestión de negocios · {tenants.length} registrados</p>
          </div>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="icono-campo" size={16} />
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-9 pr-4 text-sm font-medium focus:ring-2 focus:ring-brand-500/50 outline-none"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => { setForm(EMPTY_FORM); setError(null); setCreateModal(true); }}
            className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 shadow-lg flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={16} /> Nuevo Inquilino
          </button>
        </div>
      </div>

      {error && !createModal && !editModal && !credModal && (
        <div className="bg-error-50 text-error-600 text-sm font-medium p-4 rounded-xl border border-error-100">{error}</div>
      )}

      {/* Schemas Dormidos */}
      {dormantSchemas.length > 0 && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-alerta-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-alerta-100 bg-alerta-50/60 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database size={16} className="text-alerta-500" />
              <div>
                <p className="text-sm font-black text-alerta-800">Schemas Dormidos</p>
                <p className="text-[10px] text-alerta-600 font-medium">
                  Inquilinos archivados · datos conservados · pueden reactivarse creando el mismo nombre
                </p>
              </div>
            </div>
            <button onClick={fetchDormant} disabled={dormantLoading} className="p-2 text-alerta-400 hover:text-alerta-600 transition-colors" title="Refrescar">
              <RefreshCw size={14} className={dormantLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {dormantSchemas.map(schema => (
              <div key={schema} className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                  <RotateCcw size={14} className="text-alerta-400" />
                  <span className="font-mono text-sm font-bold text-slate-600">{schema}</span>
                  <span className="text-[10px] bg-alerta-100 text-alerta-700 px-2 py-0.5 rounded-full font-bold">Archivado</span>
                </div>
                <button
                  onClick={() => purgeSchema(schema)}
                  disabled={purgingSchema === schema}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-error-500 hover:bg-error-50 border border-error-200 transition-colors disabled:opacity-50"
                  title="Purgar permanentemente (borra todos los datos)"
                >
                  <Flame size={11} />
                  {purgingSchema === schema ? 'Purgando...' : 'Purgar'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <th className="tabla-encabezado">Negocio</th>
                <th className="tabla-encabezado">Código de Acceso</th>
                <th className="tabla-encabezado">Plan</th>
                <th className="tabla-encabezado">Vence</th>
                <th className="px-6 py-4 font-bold uppercase tracking-wider text-[11px] text-right">Estado / Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium whitespace-nowrap">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400 text-sm">No hay inquilinos registrados.</td></tr>
              )}
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-800">{t.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400 font-mono">{t.schema_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-black text-slate-700 tracking-widest bg-slate-100 px-2 py-1 rounded-lg text-xs">
                        {t.access_code}
                      </span>
                      <button
                        onClick={() => copy(t.access_code, `code-${t.id}`)}
                        className="text-slate-400 hover:text-brand-800 transition-colors"
                        title="Copiar código"
                      >
                        {copied === `code-${t.id}` ? <Check size={14} className="text-exito-500" /> : <Copy size={14} />}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {t.plan_name
                      ? <span className="bg-slate-100 px-2 py-1 rounded text-[10px] font-black text-slate-600 uppercase">{t.plan_name}</span>
                      : <span className="text-slate-300 text-xs">Sin plan</span>
                    }
                  </td>
                  <td className="px-6 py-4">
                    <VenceBadge paidUntil={t.paid_until} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end items-center gap-2">
                      <button onClick={() => openStats(t)} className="accion-fila" title="Estadísticas">
                        <BarChart2 size={16} />
                      </button>
                      <button onClick={() => { setSelectedTenant(t); setHistoryModal(true); }} className="accion-fila" title="Historial pagos">
                        <History size={16} />
                      </button>
                      <button onClick={() => openCredReset(t)} className="p-2 text-slate-300 hover:text-alerta-500 transition-colors" title="Resetear contraseña del admin">
                        <Key size={16} />
                      </button>
                      <button onClick={() => openEdit(t)} className="accion-fila" title="Editar">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => { setDeleteModal({ tenant: t }); setDeleteConfirmText(''); }} className="p-2 text-slate-300 hover:text-error-500 transition-colors" title="Eliminar inquilino">
                        <Trash2 size={16} />
                      </button>
                      <button
                        onClick={() => handleToggle(t)}
                        className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest border transition-colors ${t.is_active
                          /* Estado, no accion: verde contra rojo es la lectura inmediata
                             de activo contra suspendido. */
                          ? 'border-exito-500/30 text-exito-600 bg-exito-50 hover:bg-exito-100'
                          : 'border-error-500/30 text-error-600 bg-error-50 hover:bg-error-100'
                        }`}
                      >
                        {t.is_active ? 'ACTIVO' : 'SUSPENDIDO'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal: Crear ferretería ─────────────────────────────────────────── */}
      <AnimatePresence>
        {createModal && (
          <Overlay onClose={() => setCreateModal(false)}>
            <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-1">Nuevo Inquilino</h3>
            <p className="text-xs text-slate-400 mb-6">El código de acceso y el esquema se generan automáticamente.</p>

            {error && <ErrorBox msg={error} />}

            <form onSubmit={handleCreate} className="space-y-4">

              {/* Reactivar schema dormido */}
              {dormantSchemas.length > 0 && (
                <div className={`rounded-2xl p-4 border-2 transition-colors ${form.schema_name ? 'border-alerta-400 bg-alerta-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <RotateCcw size={14} className="text-alerta-500" />
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Reactivar inquilino archivado (opcional)</span>
                  </div>
                  <select
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold focus:ring-2 focus:ring-alerta-400/50 outline-none"
                    value={form.schema_name}
                    onChange={e => set('schema_name', e.target.value)}
                  >
                    <option value="">— Nuevo inquilino (schema nuevo) —</option>
                    {dormantSchemas.map(s => (
                      <option key={s} value={s}>{s} · recupera datos anteriores</option>
                    ))}
                  </select>
                  {form.schema_name && (
                    <p className="text-[11px] text-alerta-700 font-medium mt-2 flex items-center gap-1">
                      <Database size={11} /> Se reutilizará el schema <strong>{form.schema_name}</strong> con todos sus datos históricos.
                    </p>
                  )}
                </div>
              )}

              <Field label="Nombre del Negocio *">
                <input required type="text" placeholder="Ej: Tienda Central" className={ICLS} value={form.name} onChange={e => set('name', e.target.value)} />
              </Field>

              <Field label="Plan de Suscripción">
                <select className={ICLS} value={form.plan} onChange={e => set('plan', e.target.value)}>
                  <option value="">Sin plan asignado</option>
                  {planes.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </Field>

              <Field label="Email del Administrador *">
                <input required type="email" placeholder="admin@negocio.com" className={ICLS} value={form.admin_email} onChange={e => set('admin_email', e.target.value)} />
              </Field>

              <Field label="Contraseña del Administrador (opcional)">
                <input type="password" placeholder="Se genera automáticamente si se deja vacío" className={ICLS} value={form.admin_password} onChange={e => set('admin_password', e.target.value)} />
              </Field>

              <Field label="Fecha de Vencimiento (opcional)">
                <input type="date" className={ICLS} value={form.paid_until} onChange={e => set('paid_until', e.target.value)} />
                <p className="text-[10px] text-slate-400 mt-1 px-1">Si no se indica, se calcula automáticamente según el plan.</p>
              </Field>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setCreateModal(false)} className={BTN_SEC}>Cancelar</button>
                <button disabled={loading} className={BTN_PRI}>{loading ? 'Creando...' : 'Crear Inquilino'}</button>
              </div>
            </form>
          </Overlay>
        )}
      </AnimatePresence>

      {/* ── Modal: Editar ferretería ────────────────────────────────────────── */}
      <AnimatePresence>
        {editModal && (
          <Overlay onClose={() => setEditModal(false)}>
            <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-6">Editar Inquilino</h3>

            {error && <ErrorBox msg={error} />}

            <form onSubmit={handleEdit} className="space-y-4">
              <Field label="Nombre">
                <input required type="text" className={ICLS} value={form.name} onChange={e => set('name', e.target.value)} />
              </Field>

              <Field label="Plan">
                <select className={ICLS} value={form.plan} onChange={e => set('plan', e.target.value)}>
                  <option value="">Sin plan</option>
                  {planes.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </Field>

              <Field label="Fecha de Vencimiento">
                <input type="date" className={ICLS} value={form.paid_until} onChange={e => set('paid_until', e.target.value)} />
                <p className="text-[10px] text-slate-400 mt-1 px-1">Dejá vacío para quitar la fecha de vencimiento.</p>
              </Field>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditModal(false)} className={BTN_SEC}>Cancelar</button>
                <button disabled={loading} className={BTN_PRI}>{loading ? 'Guardando...' : 'Guardar Cambios'}</button>
              </div>
            </form>
          </Overlay>
        )}
      </AnimatePresence>

      {/* ── Modal: Resetear contraseña admin tenant ─────────────────────────── */}
      <AnimatePresence>
        {credModal && selectedTenant && (
          <Overlay onClose={() => setCredModal(false)}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-alerta-50 p-3 rounded-xl text-alerta-500"><ShieldAlert size={20} /></div>
              <div>
                <h3 className="text-xl font-black text-slate-800">Resetear Contraseña Admin</h3>
                <p className="text-xs font-bold text-slate-700">{selectedTenant.name}</p>
                {adminInfo?.email
                  ? <p className="text-[11px] text-slate-400 font-mono mt-0.5">{adminInfo.email}</p>
                  : <p className="text-[11px] text-slate-300 mt-0.5">Cargando email...</p>
                }
              </div>
            </div>

            {error && <ErrorBox msg={error} />}

            <form onSubmit={handleCredReset} className="space-y-4">
              <Field label="Nueva Contraseña *">
                <input required type="password" minLength={6} className={ICLS} value={credForm.newPassword} onChange={e => setCredForm(p => ({ ...p, newPassword: e.target.value }))} />
              </Field>
              <Field label="Confirmar Contraseña *">
                <input required type="password" className={ICLS} value={credForm.confirm} onChange={e => setCredForm(p => ({ ...p, confirm: e.target.value }))} />
              </Field>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setCredModal(false)} className={BTN_SEC}>Cancelar</button>
                <button disabled={loading} className={BTN_PRI}>{loading ? 'Actualizando...' : 'Cambiar Contraseña'}</button>
              </div>
            </form>
          </Overlay>
        )}
      </AnimatePresence>

      {/* ── Modal: Confirmar suspender / reactivar ────────────────────────────── */}
      <AnimatePresence>
        {confirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-8"
            >
              {confirmModal.tenant.is_active ? (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-error-100 p-3 rounded-xl text-error-600"><Ban size={22} /></div>
                    <div>
                      <h3 className="text-xl font-black text-slate-800">Suspender Inquilino</h3>
                      <p className="text-xs font-bold text-error-500">{confirmModal.tenant.name}</p>
                    </div>
                  </div>
                  <div className="bg-error-50 border border-error-200 rounded-2xl p-4 mb-6">
                    <div className="flex gap-2 items-start">
                      <AlertTriangle size={16} className="text-error-500 shrink-0 mt-0.5" />
                      <div className="text-sm text-error-700 font-medium space-y-1">
                        <p className="font-black">Consecuencias inmediatas:</p>
                        <ul className="list-disc list-inside text-xs space-y-0.5 text-error-600">
                          <li>Todos los usuarios perderán acceso al instante</li>
                          <li>Las sesiones activas quedarán bloqueadas</li>
                          <li>Los datos se conservan intactos</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setConfirmModal(null)} className={BTN_SEC}>Cancelar</button>
                    <button
                      onClick={executeToggle}
                      disabled={loading}
                      className="flex-1 bg-error-600 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50 hover:bg-error-700 transition-colors"
                    >
                      {loading ? 'Suspendiendo...' : 'Sí, Suspender'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-exito-100 p-3 rounded-xl text-exito-600"><ShieldCheck size={22} /></div>
                    <div>
                      <h3 className="text-xl font-black text-slate-800">Reactivar Inquilino</h3>
                      <p className="text-xs font-bold text-exito-600">{confirmModal.tenant.name}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Los usuarios podrán volver a ingresar al sistema con sus credenciales habituales.
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setConfirmModal(null)} className={BTN_SEC}>Cancelar</button>
                    <button
                      onClick={executeToggle}
                      disabled={loading}
                      className="flex-1 bg-brand-600 text-negro-950 font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50 hover:bg-brand-700 transition-colors"
                    >
                      {loading ? 'Activando...' : 'Sí, Reactivar'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal: Éxito reset contraseña ────────────────────────────────────── */}
      <AnimatePresence>
        {credSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-alerta-100 rounded-full mb-4">
                  <Key size={28} className="text-alerta-600" />
                </div>
                <h3 className="text-2xl font-black text-slate-800">Contraseña Actualizada</h3>
                <p className="text-slate-500 text-sm mt-1">Compartí estas credenciales con el administrador del negocio</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-5 space-y-3 border border-slate-200">
                <CredRow label="Negocio" value={credSuccessModal.tenantName} copyKey="cs-name" copy={copy} copied={copied} />
                <CredRow label="Email Admin" value={credSuccessModal.email} copyKey="cs-email" copy={copy} copied={copied} />
                <CredRow label="Nueva Contraseña" value={credSuccessModal.password} copyKey="cs-pw" copy={copy} copied={copied} mono />
              </div>

              <p className="text-xs text-slate-400 text-center mt-4">Esta contraseña no se volverá a mostrar.</p>

              <button
                onClick={() => setCredSuccessModal(null)}
                className="w-full mt-6 bg-slate-900 text-white font-black uppercase tracking-widest py-3 rounded-2xl"
              >
                Listo
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal: Éxito - mostrar credenciales ─────────────────────────────── */}
      <AnimatePresence>
        {successModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-exito-100 rounded-full mb-4">
                  <Check size={28} className="text-exito-600" />
                </div>
                <h3 className="text-2xl font-black text-slate-800">¡Inquilino Creado!</h3>
                <p className="text-slate-500 text-sm mt-1">Compartí estas credenciales con el cliente</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-5 space-y-3 border border-slate-200">
                <CredRow label="Nombre" value={successModal.name} copyKey="name" copy={copy} copied={copied} />
                <CredRow label="Código de Acceso" value={successModal.access_code} copyKey="ac" copy={copy} copied={copied} mono />
                <CredRow label="Email Admin" value={successModal.admin_email} copyKey="em" copy={copy} copied={copied} />
                <CredRow label="Contraseña Admin" value={successModal.admin_password} copyKey="pw" copy={copy} copied={copied} mono />
                <CredRow label="URL de Login" value={successModal.login_url} copyKey="url" copy={copy} copied={copied} />
              </div>

              <p className="text-xs text-slate-400 text-center mt-4">Guardá esta información — la contraseña no se mostrará de nuevo.</p>

              <button
                onClick={() => setSuccessModal(null)}
                className="w-full mt-6 bg-slate-900 text-white font-black uppercase tracking-widest py-3 rounded-2xl"
              >
                Listo
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal: Estadísticas del tenant ───────────────────────────────────── */}
      <AnimatePresence>
        {statsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl p-8"
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-black text-slate-800 tracking-tight">Estadísticas</h3>
                  <p className="text-xs font-bold text-brand-800 uppercase tracking-widest">{statsModal.tenant.name}</p>
                </div>
                <button onClick={() => setStatsModal(null)} className="bg-slate-100 p-2 rounded-full hover:bg-slate-200"><X size={20} /></button>
              </div>

              {statsLoading && (
                <div className="text-center py-12 text-slate-400">
                  <RefreshCw size={28} className="mx-auto mb-3 animate-spin opacity-40" />
                  <p className="text-sm font-medium">Cargando estadísticas...</p>
                </div>
              )}

              {!statsLoading && statsModal.data === 'error' && (
                <div className="text-center py-12 text-error-400">
                  <p className="text-sm font-medium">No se pudieron cargar las estadísticas.</p>
                </div>
              )}

              {!statsLoading && statsModal.data && statsModal.data !== 'error' && (() => {
                const d = statsModal.data;
                const fmt = n => parseFloat(n).toLocaleString('es-PY');
                return (
                  <div className="space-y-4">
                    {/* KPIs principales */}
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard icon={<ShoppingCart size={18} className="text-exito-600" />} label="Ventas del mes" value={`Gs. ${fmt(d.ventas_mes)}`} sub={`${d.cantidad_mes} transacciones`} color="marca" />
                      <StatCard icon={<TrendingUp size={18} className="text-brand-800" />} label="Total facturado" value={`Gs. ${fmt(d.total_facturado)}`} sub={`${d.total_ventas} ventas históricas`} color="marca" />
                      <StatCard icon={<Package size={18} className="text-brand-800" />} label="Productos activos" value={d.total_productos} sub={d.bajo_stock > 0 ? `⚠ ${d.bajo_stock} bajo mínimo` : 'Stock OK'} color="marca" />
                      <StatCard icon={<Users size={18} className="text-brand-800" />} label="Usuarios / Clientes" value={`${d.total_usuarios} / ${d.total_clientes}`} sub="usuarios activos / clientes" color="marca" />
                    </div>

                    {/* Última venta */}
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex items-center justify-between">
                      <span className="titulo-seccion">Última venta</span>
                      <span className="text-sm font-bold text-slate-700">
                        {d.ultima_venta
                          ? new Date(d.ultima_venta).toLocaleString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : 'Sin ventas registradas'}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <button onClick={() => setStatsModal(null)} className="w-full mt-6 bg-slate-100 text-slate-600 font-black uppercase tracking-widest py-3 rounded-2xl text-xs hover:bg-slate-200 transition-colors">
                Cerrar
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal: Confirmar eliminación ─────────────────────────────────────── */}
      <AnimatePresence>
        {deleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8"
            >
              {/* Cabecera */}
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-error-100 p-3 rounded-xl text-error-600"><Trash2 size={22} /></div>
                <div>
                  <h3 className="text-xl font-black text-slate-800">Eliminar Inquilino</h3>
                  <p className="text-xs font-bold text-error-500 font-mono">{deleteModal.tenant.schema_name}</p>
                </div>
              </div>

              {/* Advertencia */}
              <div className="space-y-3 mb-5">
                <div className="bg-alerta-50 border border-alerta-200 rounded-2xl p-4">
                  <div className="flex gap-2 items-start">
                    <AlertTriangle size={16} className="text-alerta-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-black text-alerta-700">El acceso queda bloqueado inmediatamente:</p>
                      <ul className="list-disc list-inside text-xs space-y-0.5 text-alerta-600 font-medium">
                        <li>El inquilino y sus usuarios no podrán ingresar</li>
                        <li>El código de acceso queda liberado</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="bg-exito-50 border border-exito-200 rounded-2xl p-4">
                  <div className="flex gap-2 items-start">
                    <Database size={16} className="text-exito-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-black text-exito-700">Los datos se conservan en la base de datos:</p>
                      <ul className="list-disc list-inside text-xs space-y-0.5 text-exito-600 font-medium">
                        <li>Productos, ventas, clientes e historial quedan intactos</li>
                        <li>Si el inquilino vuelve, recupera todo su historial</li>
                        <li>Para borrar definitivamente, usá "Schemas Dormidos"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmación por nombre */}
              <div className="mb-5">
                <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                  Escribí el nombre del negocio para confirmar:
                </p>
                <p className="text-sm font-black text-slate-800 bg-slate-100 rounded-xl px-3 py-2 mb-3 font-mono tracking-wide">
                  {deleteModal.tenant.name}
                </p>
                <input
                  type="text"
                  placeholder={`Escribí "${deleteModal.tenant.name}"`}
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  className="w-full bg-white border-2 border-slate-200 focus:border-error-400 rounded-xl px-4 py-3 text-sm font-bold outline-none transition-colors"
                  autoComplete="off"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }}
                  className={BTN_SEC}
                >
                  Cancelar
                </button>
                <button
                  onClick={executeDelete}
                  disabled={loading || deleteConfirmText !== deleteModal.tenant.name}
                  className="flex-1 bg-alerta-600 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs transition-colors hover:bg-alerta-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {loading ? 'Archivando...' : 'Archivar Inquilino'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal: Historial ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {historyModal && selectedTenant && (
          <div className="fondo-modal">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl p-8 max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-black text-slate-800 tracking-tight">Historial del Cliente</h3>
                  <p className="text-xs font-bold text-brand-800 uppercase tracking-widest">{selectedTenant.name}</p>
                </div>
                <button onClick={() => setHistoryModal(false)} className="bg-slate-100 p-2 rounded-full hover:bg-slate-200"><X size={20} /></button>
              </div>

              <div className="overflow-y-auto space-y-8">
                {/* Suscripciones */}
                <Section title="Historial de Suscripciones" icon={<RefreshCw size={14} />}>
                  {selectedTenant.historial_suscripciones?.length > 0
                    ? selectedTenant.historial_suscripciones.map(h => (
                      <div key={h.id} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex justify-between items-center">
                        <div>
                          <p className="font-black text-slate-800 text-sm">{h.nombre_plan}</p>
                          <p className="text-[10px] text-slate-500">{h.fecha_format || new Date(h.fecha_cambio).toLocaleString('es-PY')}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm">{h.moneda} {parseFloat(h.precio_contratado).toLocaleString()}</p>
                          <p className="text-[9px] text-slate-400 italic">{h.motivo}</p>
                        </div>
                      </div>
                    ))
                    : <EmptyState text="Sin cambios de plan registrados." />
                  }
                </Section>

                {/* Pagos */}
                <Section title="Registro de Cobranzas" icon={<CreditCard size={14} />}>
                  {selectedTenant.pagos?.length > 0
                    ? selectedTenant.pagos.map(p => (
                      <div key={p.id} className="bg-exito-50/50 p-4 rounded-2xl border border-exito-100/50 flex justify-between items-center">
                        <div>
                          <p className="font-black text-slate-800 text-sm">Periodo: {p.mes_referencia}/{p.anio_referencia}</p>
                          <p className="text-[10px] text-exito-600 font-bold">Factura: {p.nro_comprobante || 'S/N'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-exito-700 text-sm">Gs. {parseFloat(p.monto).toLocaleString()}</p>
                          <p className="text-[9px] text-slate-400">{p.forma_pago} · {new Date(p.fecha_pago).toLocaleDateString('es-PY')}</p>
                        </div>
                      </div>
                    ))
                    : <EmptyState text="Sin pagos registrados." />
                  }
                </Section>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex justify-between items-center">
                <div className="bg-slate-900 px-4 py-2 rounded-xl text-white">
                  <p className="text-[9px] font-bold uppercase opacity-60">Total Recaudado</p>
                  <p className="text-lg font-black">Gs. {(selectedTenant.pagos?.reduce((acc, p) => acc + parseFloat(p.monto), 0) || 0).toLocaleString()}</p>
                </div>
                <button onClick={() => setHistoryModal(false)} className="bg-slate-100 px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200">Cerrar</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function VenceBadge({ paidUntil }) {
  if (!paidUntil) return <span className="text-slate-300 text-xs">—</span>;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const vence = new Date(paidUntil);
  vence.setHours(0, 0, 0, 0);
  const dias = Math.round((vence - hoy) / (1000 * 60 * 60 * 24));
  const fechaFmt = vence.toLocaleDateString('es-PY');

  if (dias < 0) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-error-500 font-bold">{fechaFmt}</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-black text-error-600 bg-error-50 border border-error-200 px-2 py-0.5 rounded-full w-fit">
          <AlertTriangle size={9} /> VENCIDO
        </span>
      </div>
    );
  }
  if (dias <= 5) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-alerta-600 font-bold">{fechaFmt}</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-black text-alerta-700 bg-alerta-50 border border-alerta-200 px-2 py-0.5 rounded-full w-fit">
          <Clock size={9} /> {dias === 0 ? 'Vence hoy' : `${dias}d restante${dias !== 1 ? 's' : ''}`}
        </span>
      </div>
    );
  }
  return <span className="text-xs text-slate-500 font-medium">{fechaFmt}</span>;
}

function Overlay({ children, onClose }) {
  return (
    <div className="fondo-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-8 relative"
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-300 hover:text-slate-500"><X size={20} /></button>
        {children}
      </motion.div>
    </div>
  );
}


function ErrorBox({ msg }) {
  return <div className="aviso-error mb-4">{msg}</div>;
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h4 className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest mb-3">{icon} {title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="text-center py-4 text-xs font-bold text-slate-400 italic">{text}</p>;
}

// Un color por tarjeta no informaba nada: cada resumen ya se distingue por su
// titulo y su icono. El verde se reserva para lo que salio bien.
const COLOR_MAP = {
  marca:  'bg-brand-50 border-brand-100',
  neutro: 'bg-slate-50 border-slate-100',
};

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className={`rounded-2xl p-4 border ${COLOR_MAP[color] || 'bg-slate-50 border-slate-100'}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span></div>
      <p className="text-xl font-black text-slate-800 leading-tight">{value}</p>
      <p className="text-[10px] text-slate-400 font-medium mt-0.5">{sub}</p>
    </div>
  );
}

function CredRow({ label, value, copyKey, copy, copied, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest w-28 shrink-0">{label}</span>
      <div className="flex items-center gap-2 overflow-hidden">
        <span className={`text-sm font-bold text-slate-800 truncate ${mono ? 'font-mono tracking-wider' : ''}`}>{value}</span>
        <button onClick={() => copy(value, copyKey)} className="text-slate-400 hover:text-brand-800 shrink-0">
          {copied === copyKey ? <Check size={14} className="text-exito-500" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

const ICLS = 'campo';
const BTN_PRI = 'boton-principal flex-1';
const BTN_SEC = 'boton-secundario flex-1';
